/**
 * Product page behaviour: variant resolution, gallery sync, mobile slider and
 * the size-chart modal. Vanilla ES2019+, no dependencies. Every query is
 * guarded so a missing element (a merchant removed a block, or this script
 * loads on a page with no product form) never throws.
 */
(function () {
  'use strict';

  /* ─── Variant resolution ────────────────────────────────────────────────── */

  function getOptionCount(variants) {
    var count = 0;
    for (var i = 0; i < variants.length; i++) {
      if (variants[i].options && variants[i].options.length > count) {
        count = variants[i].options.length;
      }
    }
    return count;
  }

  function getSelectedOptions(root, optionCount) {
    var options = new Array(optionCount).fill(null);
    var inputs = root.querySelectorAll('[data-option-input]:checked');
    inputs.forEach(function (input) {
      var position = parseInt(input.getAttribute('data-option-position'), 10);
      if (!isNaN(position) && position >= 1 && position <= optionCount) {
        options[position - 1] = input.value;
      }
    });
    return options;
  }

  function findVariant(variants, selectedOptions) {
    for (var i = 0; i < variants.length; i++) {
      var variant = variants[i];
      var matches = true;
      for (var j = 0; j < selectedOptions.length; j++) {
        var selected = selectedOptions[j];
        if (selected != null && variant.options[j] !== selected) {
          matches = false;
          break;
        }
      }
      if (matches) return variant;
    }
    return null;
  }

  function formatMoney(cents) {
    if (cents == null) return '';
    var whole = Math.floor(Math.abs(cents) / 100);
    var frac = Math.abs(cents) % 100;
    var sign = cents < 0 ? '-' : '';
    return sign + whole + '.' + (frac < 10 ? '0' + frac : frac);
  }

  function updatePackCardPrices(form, variants, selectedOptions) {
    var cards = form.querySelectorAll('[data-pack-card]');
    cards.forEach(function (card) {
      var position = parseInt(card.getAttribute('data-option-position'), 10);
      var value = card.getAttribute('data-value');
      if (isNaN(position) || !value) return;

      var candidateOptions = selectedOptions.slice();
      candidateOptions[position - 1] = value;
      var variant = findVariant(variants, candidateOptions);
      if (!variant) return;

      var priceEl = card.querySelector('[data-pack-price]');
      var compareEl = card.querySelector('[data-pack-compare]');
      var saveEl = card.querySelector('[data-pack-save]');
      var onSale = typeof variant.compare_at_price === 'number' && variant.compare_at_price > variant.price;

      if (priceEl) priceEl.textContent = formatMoney(variant.price);
      if (compareEl) {
        if (onSale) {
          compareEl.textContent = formatMoney(variant.compare_at_price);
          compareEl.removeAttribute('hidden');
        } else {
          compareEl.setAttribute('hidden', '');
        }
      }
      if (saveEl) {
        if (onSale) {
          saveEl.textContent = 'Save ' + formatMoney(variant.compare_at_price - variant.price);
          saveEl.removeAttribute('hidden');
        } else {
          saveEl.setAttribute('hidden', '');
        }
      }

      var input = form.querySelector(
        '[data-option-input][data-option-position="' + position + '"][value="' + cssEscape(value) + '"]'
      );
      if (input) input.disabled = variant.available === false;
    });
  }

  function cssEscape(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(value);
    return String(value).replace(/["\\]/g, '\\$&');
  }

  function updateAddToCartButton(form, variant, strings) {
    var button = form.querySelector('[data-add-to-cart-button]');
    var label = form.querySelector('[data-add-to-cart-text]');
    if (!button) return;
    var available = !!variant && variant.available !== false;
    button.disabled = !available;
    if (label) {
      label.textContent = available ? strings.addToCart : strings.soldOut;
    }
  }

  function updateGallery(form, variant) {
    if (!variant || !variant.featured_media) return;
    var gallery = document.querySelector('[data-product-gallery]');
    if (!gallery) return;
    var mediaId = variant.featured_media.id;
    var slide = gallery.querySelector('[data-gallery-slide][data-media-id="' + mediaId + '"]');
    if (!slide) return;
    var index = parseInt(slide.getAttribute('data-index'), 10);
    if (!isNaN(index)) goToSlide(gallery, index, 'auto');
  }

  function updateUrl(variant) {
    if (!variant || !window.history || !window.history.replaceState) return;
    try {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    } catch (e) {
      /* no-op: URL API unsupported or blocked */
    }
  }

  function handleOptionChange(form, variants, optionCount, strings) {
    var selectedOptions = getSelectedOptions(form, optionCount);
    var variant = findVariant(variants, selectedOptions);

    var idInput = form.querySelector('[data-product-id-input]');
    if (idInput && variant) idInput.value = variant.id;

    updateAddToCartButton(form, variant, strings);
    updateGallery(form, variant);
    updateUrl(variant);
    updatePackCardPrices(form, variants, selectedOptions);
  }

  function initVariantForms() {
    var scripts = document.querySelectorAll('[data-variant-json]');
    scripts.forEach(function (script) {
      var form = script.closest('form');
      if (!form) return;

      var variants;
      try {
        variants = JSON.parse(script.textContent);
      } catch (e) {
        return;
      }
      if (!Array.isArray(variants) || !variants.length) return;

      var optionCount = getOptionCount(variants);
      var strings = (window.themeStrings && window.themeStrings.addToCart)
        ? { addToCart: window.themeStrings.addToCart, soldOut: window.themeStrings.soldOut || 'Sold out' }
        : { addToCart: 'Add to cart', soldOut: 'Sold out' };

      form.addEventListener('change', function (event) {
        if (event.target && event.target.matches && event.target.matches('[data-option-input]')) {
          handleOptionChange(form, variants, optionCount, strings);
        }
      });

      // Prime pack-card prices/state on load in case the server-rendered
      // defaults ever drift from the JS-computed combination.
      updatePackCardPrices(form, variants, getSelectedOptions(form, optionCount));
    });
  }

  /* ─── Stock meter ───────────────────────────────────────────────────────── */

  /*
   * Fills the bar from 0 to its real percentage the first time it is scrolled
   * into view. The server already renders the correct width, so this only ever
   * removes and re-applies it — with JS off the bar is simply static and right.
   */
  function initStockMeters() {
    var bars = document.querySelectorAll('[data-stock-fill]');
    if (!bars.length) return;

    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    bars.forEach(function (bar) {
      if (bar.dataset.stockReady) return;
      bar.dataset.stockReady = '1';

      var target = bar.getAttribute('data-target');
      if (target === null || reduced) return;

      /*
       * Fills on load, unconditionally.
       *
       * Two earlier attempts gated this on visibility — first an
       * IntersectionObserver, then a scroll listener — so the fill would not be
       * missed when the meter sits below the fold. Neither fired reliably on
       * this page, and a defer-until-seen that never fires is worse than an
       * animation someone might scroll past: the bar just sat at its final
       * width. Running it on load is what was asked for and is verifiable.
       */
      /*
       * The two writes must land in separate tasks with a paint between them.
       * Doing both in one task — even with a forced reflow via offsetWidth —
       * was measurably not enough: the browser resolved straight to the final
       * width with no transition. Collapsing first, then expanding on a later
       * timer, is what actually animates.
       */
      window.setTimeout(function () {
        // Snap to zero with the transition off, or the bar visibly shrinks from
        // its rendered width first and the fill reads as a bounce.
        bar.style.transition = 'none';
        bar.style.width = '0%';

        /*
         * Restore the transition and expand in a later task. Both writes in one
         * task — even with a forced reflow — resolved straight to the final
         * width with no animation at all.
         */
        window.setTimeout(function () {
          bar.style.transition = '';
          bar.style.width = target + '%';
        }, 60);
      }, 80);
    });
  }

  /* ─── Option labels ─────────────────────────────────────────────────────── */

  /*
   * Keeps "Size: M" / "Color: Nude" in step with the chosen radio.
   *
   * Deliberately separate from the variant lookup: an option value with no
   * purchasable variant (a sold-out size, or a size not stocked in the current
   * colour) still needs its label to reflect what the shopper just clicked.
   */
  var optionLabelsBound = false;

  function initOptionLabels() {
    // Delegated on document, so bind once no matter how often init() runs.
    if (optionLabelsBound) return;
    optionLabelsBound = true;

    document.addEventListener('change', function (event) {
      var input = event.target;
      if (!input || !input.matches || !input.matches('[data-option-input]')) return;

      var row = input.closest('.variant-option');
      if (!row) return;
      var label = row.querySelector('[data-option-selected-label]');
      if (label) label.textContent = input.value;
    });
  }

  /* ─── "Size M fits: …" ──────────────────────────────────────────────────── */

  /*
   * Swaps the bra-size list under the size buttons as the shopper changes size.
   * Independent of the variant JSON on purpose: the line describes the size
   * option itself, so it must still update for products whose size values have
   * no matching variant (sold out, or not stocked in every colour).
   */
  function initSizeFits() {
    document.querySelectorAll('[data-size-fits]').forEach(function (el) {
      if (el.dataset.sizeFitsReady) return;
      el.dataset.sizeFitsReady = '1';

      var map;
      try {
        map = JSON.parse(el.getAttribute('data-size-fits-map') || '{}');
      } catch (e) {
        return;
      }

      // Lower-cased index so "2xl", "XXL" and "2XL" all resolve.
      var index = {};
      Object.keys(map).forEach(function (k) {
        index[normaliseSizeKey(k)] = map[k];
      });
      if (!Object.keys(index).length) return;

      var position = el.getAttribute('data-option-position');
      var template = el.getAttribute('data-size-fits-template') || 'Size [size] fits:';
      var labelEl = el.querySelector('[data-size-fits-label]');
      var valueEl = el.querySelector('[data-size-fits-value]');
      var root = el.closest('form') || el.closest('.product-info') || document;

      function apply(size) {
        var fits = size ? index[normaliseSizeKey(size)] : null;
        if (!fits) {
          el.hidden = true;
          return;
        }
        if (labelEl) labelEl.textContent = template.replace('[size]', size);
        if (valueEl) valueEl.textContent = fits;
        el.hidden = false;
      }

      root.addEventListener('change', function (event) {
        var input = event.target;
        if (!input || !input.matches || !input.matches('[data-option-input]')) return;
        if (input.getAttribute('data-option-position') !== position) return;
        apply(input.value);
      });

      var checked = root.querySelector(
        '[data-option-input][data-option-position="' + position + '"]:checked'
      );
      if (checked) apply(checked.value);
    });
  }

  /*
   * XXL/2XL and stray punctuation are the two ways a merchant's map keys drift
   * from their actual option values, so both are normalised away.
   */
  function normaliseSizeKey(value) {
    var key = String(value).toLowerCase().replace(/[\s._-]/g, '');
    var repeats = key.match(/^(x+)l$/);
    if (repeats) return repeats[1].length + 'xl';
    var numeric = key.match(/^(\d+)x l?$/) || key.match(/^(\d+)xl$/);
    if (numeric) return numeric[1] + 'xl';
    return key;
  }

  /* ─── Quantity stepper ──────────────────────────────────────────────────── */

  function initQuantitySteppers() {
    document.querySelectorAll('[data-quantity-decrease]').forEach(function (button) {
      button.addEventListener('click', function () {
        var input = button.parentElement && button.parentElement.querySelector('[data-quantity-input]');
        if (!input) return;
        var value = Math.max(1, (parseInt(input.value, 10) || 1) - 1);
        input.value = value;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
    document.querySelectorAll('[data-quantity-increase]').forEach(function (button) {
      button.addEventListener('click', function () {
        var input = button.parentElement && button.parentElement.querySelector('[data-quantity-input]');
        if (!input) return;
        var value = (parseInt(input.value, 10) || 1) + 1;
        input.value = value;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  /* ─── Gallery: thumbs, slider track, dots ───────────────────────────────── */

  /*
   * Brings a thumbnail into view by scrolling ONLY its own strip.
   *
   * scrollIntoView() walks every scrollable ancestor including the document, so
   * calling it on a mobile thumbnail scrolled the whole page up to the gallery.
   * Setting scrollLeft on the strip is contained by construction.
   */
  function revealThumb(thumb) {
    var strip = thumb.parentElement;
    if (!strip) return;
    if (strip.scrollWidth <= strip.clientWidth) return; // nothing to scroll
    var target = thumb.offsetLeft - (strip.clientWidth - thumb.offsetWidth) / 2;
    var max = strip.scrollWidth - strip.clientWidth;
    strip.scrollLeft = Math.max(0, Math.min(target, max));
  }

  function goToSlide(gallery, index, behavior) {
    var stage = gallery.querySelector('[data-gallery-stage]');
    var track = gallery.querySelector('[data-gallery-track]');
    if (!stage || !track) return;
    var slides = track.querySelectorAll('[data-gallery-slide]');
    if (!slides.length) return;
    var clamped = Math.max(0, Math.min(index, slides.length - 1));

    stage.scrollTo({ left: clamped * stage.clientWidth, behavior: behavior || 'auto' });

    gallery.querySelectorAll('[data-gallery-thumb]').forEach(function (thumb) {
      var isActive = parseInt(thumb.getAttribute('data-index'), 10) === clamped;
      thumb.classList.toggle('is-active', isActive);
      thumb.setAttribute('aria-selected', isActive ? 'true' : 'false');
      if (isActive) {
        thumb.setAttribute('aria-current', 'true');
        revealThumb(thumb);
      } else {
        thumb.removeAttribute('aria-current');
      }
    });

    gallery.querySelectorAll('[data-gallery-dot]').forEach(function (dot) {
      dot.classList.toggle('is-active', parseInt(dot.getAttribute('data-index'), 10) === clamped);
    });
  }

  function initGalleries() {
    document.querySelectorAll('[data-product-gallery]').forEach(function (gallery) {
      var stage = gallery.querySelector('[data-gallery-stage]');

      gallery.querySelectorAll('[data-gallery-thumb]').forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          var index = parseInt(thumb.getAttribute('data-index'), 10);
          if (!isNaN(index)) goToSlide(gallery, index, 'smooth');
        });
      });

      gallery.querySelectorAll('[data-gallery-dot]').forEach(function (dot) {
        dot.addEventListener('click', function () {
          var index = parseInt(dot.getAttribute('data-index'), 10);
          if (!isNaN(index)) goToSlide(gallery, index, 'smooth');
        });
      });

      if (!stage) return;
      var scrollTimeout;
      stage.addEventListener('scroll', function () {
        window.clearTimeout(scrollTimeout);
        scrollTimeout = window.setTimeout(function () {
          if (!stage.clientWidth) return;
          var index = Math.round(stage.scrollLeft / stage.clientWidth);
          gallery.querySelectorAll('[data-gallery-dot]').forEach(function (dot) {
            dot.classList.toggle('is-active', parseInt(dot.getAttribute('data-index'), 10) === index);
          });
          gallery.querySelectorAll('[data-gallery-thumb]').forEach(function (thumb) {
            var isActive = parseInt(thumb.getAttribute('data-index'), 10) === index;
            thumb.classList.toggle('is-active', isActive);
            // Keep the highlighted thumb visible in the mobile strip, which is
            // narrower than the full set of thumbnails.
            if (isActive) revealThumb(thumb);
          });
        }, 100);
      });

      /*
       * Width only. Mobile browsers fire `resize` every time the URL bar slides
       * in or out — that is a height change, and re-running the slide sync on it
       * meant the gallery re-positioned itself repeatedly while simply scrolling
       * the page.
       */
      var lastWidth = window.innerWidth;
      window.addEventListener('resize', function () {
        if (window.innerWidth === lastWidth) return;
        lastWidth = window.innerWidth;

        var activeDot = gallery.querySelector('[data-gallery-dot].is-active');
        if (activeDot) {
          var index = parseInt(activeDot.getAttribute('data-index'), 10);
          if (!isNaN(index)) goToSlide(gallery, index, 'auto');
        }
      });
    });
  }

  /* ─── Size-chart modal ──────────────────────────────────────────────────── */

  function getSizeChartModal() {
    return document.querySelector('[data-size-chart-modal]') || document.getElementById('SizeChartModal');
  }

  function openSizeChartModal(trigger) {
    var modal = getSizeChartModal();
    if (!modal) return;
    modal.removeAttribute('hidden');
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    modal._returnFocusTo = trigger || null;

    var focusable = modal.querySelector(
      'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusable) focusable.focus();

    document.addEventListener('keydown', onModalKeydown);
  }

  function closeSizeChartModal() {
    var modal = getSizeChartModal();
    if (!modal) return;
    modal.setAttribute('hidden', '');
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.removeEventListener('keydown', onModalKeydown);
    if (modal._returnFocusTo && typeof modal._returnFocusTo.focus === 'function') {
      modal._returnFocusTo.focus();
    }
    modal._returnFocusTo = null;
  }

  function onModalKeydown(event) {
    if (event.key === 'Escape' || event.key === 'Esc') {
      closeSizeChartModal();
    }
  }

  function initSizeChartModal() {
    document.querySelectorAll('[data-size-chart-open]').forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        openSizeChartModal(trigger);
      });
    });
    document.querySelectorAll('[data-size-chart-close]').forEach(function (closeButton) {
      closeButton.addEventListener('click', closeSizeChartModal);
    });
    var modal = getSizeChartModal();
    if (modal) {
      modal.addEventListener('click', function (event) {
        if (event.target === modal) closeSizeChartModal();
      });
    }
  }

  /* ─── Boot ──────────────────────────────────────────────────────────────── */

  function init() {
    initSizeFits();
    initOptionLabels();
    initStockMeters();
    initVariantForms();
    initQuantitySteppers();
    initGalleries();
    initSizeChartModal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ── Mobile sticky buy bar ──────────────────────────────────────────────────
 * Proxies to the real product form rather than duplicating it: tapping a size
 * checks the matching input so the variant picker, price, gallery and hidden
 * variant id all update through the existing code path. The bar reveals itself
 * once the real add-to-cart button has scrolled out of view.
 * ------------------------------------------------------------------------- */
(function () {
  function initStickyBuy() {
    var bar = document.querySelector('[data-sticky-buy]');
    if (!bar || bar.dataset.stickyReady) return;
    bar.dataset.stickyReady = '1';

    var realAdd = document.querySelector('[data-add-to-cart-button]');
    var form = realAdd ? realAdd.closest('form') : document.querySelector('form[action*="/cart/add"]');
    if (!form) return;

    function sizeInputs() {
      return Array.prototype.slice.call(
        form.querySelectorAll('.variant-pill__input, [data-option-input]')
      );
    }

    // Tapping a sticky size checks the matching real input.
    bar.querySelectorAll('[data-sticky-size]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var wanted = btn.getAttribute('data-sticky-size');
        var match = sizeInputs().filter(function (i) { return i.value === wanted && !i.disabled; })[0];
        if (!match) return;
        match.checked = true;
        match.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });

    // Reflect the form's state back onto the bar.
    function sync() {
      var checked = sizeInputs().filter(function (i) { return i.checked; }).map(function (i) { return i.value; });
      bar.querySelectorAll('[data-sticky-size]').forEach(function (btn) {
        var on = checked.indexOf(btn.getAttribute('data-sticky-size')) !== -1;
        btn.classList.toggle('is-selected', on);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      var priceEl = bar.querySelector('[data-sticky-price]');
      var livePrice = document.querySelector('[data-product-price] .price__whole, .price__whole');
      if (priceEl && livePrice) {
        var wrap = livePrice.closest('.price');
        if (wrap) priceEl.textContent = wrap.textContent.replace(/\s+/g, ' ').trim().split(' ').pop();
      }
      var textEl = bar.querySelector('[data-sticky-add-text]');
      var realText = document.querySelector('[data-add-to-cart-text]');
      var addBtn = bar.querySelector('[data-sticky-add]');
      if (textEl && realText) textEl.textContent = realText.textContent;
      if (addBtn && realAdd) addBtn.disabled = realAdd.disabled;
    }

    form.addEventListener('change', sync);
    sync();

    // Submitting the real form keeps every existing cart behaviour intact.
    var addBtn = bar.querySelector('[data-sticky-add]');
    if (addBtn) {
      addBtn.addEventListener('click', function () {
        if (realAdd) realAdd.click();
        else form.submit();
      });
    }

    // Reserve space at the page bottom so the fixed bar never covers the footer.
    document.body.classList.add('sticky-buy-active');

    // Show the bar only once the real button is off screen.
    if ('IntersectionObserver' in window && realAdd) {
      bar.hidden = false;
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          bar.classList.toggle('is-visible', !entry.isIntersecting);
        });
      }, { rootMargin: '0px 0px -40px 0px' });
      io.observe(realAdd);
    } else {
      bar.hidden = false;
      bar.classList.add('is-visible');
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initStickyBuy);
  else initStickyBuy();
  document.addEventListener('shopify:section:load', initStickyBuy);

  // Re-scan after a theme-editor section reload: the fresh markup has lost the
  // per-element "ready" flags these two set.
  document.addEventListener('shopify:section:load', function () {
    initSizeFits();
    initOptionLabels();
    initStockMeters();
  });
})();
